import React, { useEffect, useState } from "react";
import {
  createCycleEngagementReview,
  getCycleEngagementConfiguration,
  getCycleEngagements,
  getUsersList,
  saveCycleEngagementAssignments,
  saveCycleEngagementChecklists,
  selectCycleEngagementTemplate,
} from "../../helpers/backend_helper";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";

export default function CycleEngagementReviewConfiguration({
  cycleId,
  engagementId,
  onCreated,
}) {
  const [linkId, setLinkId] = useState(null);
  const [configuration, setConfiguration] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedChecklists, setSelectedChecklists] = useState([]);
  const [reviewerId, setReviewerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadConfiguration = async (id) => {
    const response = await getCycleEngagementConfiguration(id);
    if (!response?.status)
      throw new Error(
        response?.message || "Unable to load review configuration."
      );
    setConfiguration(response.data);
    setSelectedChecklists(
      (response.data.link?.checklists || []).map((item) =>
        Number(item.checklistId)
      )
    );
    setReviewerId(
      String(
        response.data.link?.assignments?.find(
          (item) => item.assignmentType === "PrimaryReviewer"
        )?.userId || ""
      )
    );
  };

  useEffect(() => {
    let current = true;
    setError("");
    Promise.all([getCycleEngagements(cycleId), getUsersList()])
      .then(async ([linksResponse, usersResponse]) => {
        if (!current) return;
        const link = (linksResponse?.data || []).find(
          (item) => Number(item.engagementId) === Number(engagementId)
        );
        if (!link)
          throw new Error(
            "This engagement is not attached to the selected inspection cycle."
          );
        setLinkId(link.id);
        if (usersResponse?.status) setUsers(usersResponse.data || []);
        await loadConfiguration(link.id);
      })
      .catch((reason) => {
        if (current)
          setError(reason.message || "Unable to load review configuration.");
      });
    return () => {
      current = false;
    };
  }, [cycleId, engagementId]);

  const selectedTemplateId = configuration?.link?.reviewTemplateId || "";

  const run = async (action, success) => {
    setBusy(true);
    setError("");
    try {
      const response = await action();
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to save review configuration."
        );
      success && SuccessNotify(success);
      await loadConfiguration(linkId);
      return response;
    } catch (reason) {
      setError(reason.message || "Unable to save review configuration.");
      ErrorNotify(reason.message || "Unable to save review configuration.");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const saveTeam = () => {
    if (!reviewerId) {
      setError("Select a primary reviewer.");
      return;
    }
    const checklistRows = configuration?.link?.checklists || [];
    run(
      () =>
        saveCycleEngagementAssignments(
          linkId,
          checklistRows.map((item, index) => ({
            inspectionCycleChecklistId: item.id,
            assignmentType: "PrimaryReviewer",
            roleCode: "Primary Reviewer",
            userId: Number(reviewerId),
            sortOrder: index + 1,
            isRequired: true,
          }))
        ),
      "Review team saved."
    );
  };

  const createReview = async () => {
    const response = await run(
      () => createCycleEngagementReview(linkId),
      "Inspection review created."
    );
    if (response?.data) onCreated?.(response.data);
  };

  if (error && !configuration)
    return (
      <div className="inspection-journey__note" role="alert">
        {error}
      </div>
    );
  if (!configuration)
    return (
      <div className="inspection-journey__note" role="status">
        Loading review configuration…
      </div>
    );
  const link = configuration.link;
  return (
    <section
      className="inspection-journey__preparation"
      aria-label="Review configuration"
    >
      <div>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <section className="inspection-journey__panel">
          <header>
            <div>
              <h2>1. Review Template</h2>
              <p>
                PIERS matches Review Type + Industry Type + Work Type. Choose
                any published template for the same review type when
                configuration is required.
              </p>
            </div>
          </header>
          <label>Review template</label>
          <select
            className="form-select"
            value={selectedTemplateId}
            disabled={busy}
            onChange={(event) =>
              run(
                () =>
                  selectCycleEngagementTemplate(
                    linkId,
                    Number(event.target.value)
                  ),
                "Review template selected."
              )
            }
          >
            <option value="">Select a review template</option>
            {(configuration.candidateTemplates || []).map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
          <small>Match status: {link.templateMatchStatus}</small>
        </section>
        <section className="inspection-journey__panel">
          <header>
            <div>
              <h2>2. Checklists</h2>
              <p>
                Template checklists are recommendations. You can add or remove
                any checklist matching a selected cycle standard.
              </p>
            </div>
          </header>
          <div className="d-grid gap-2">
            {(configuration.eligibleChecklists || []).map((checklist) => (
              <label key={checklist.id} className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={selectedChecklists.includes(Number(checklist.id))}
                  onChange={(event) =>
                    setSelectedChecklists((current) =>
                      event.target.checked
                        ? [...current, Number(checklist.id)]
                        : current.filter((id) => id !== Number(checklist.id))
                    )
                  }
                />
                <span className="form-check-label">{checklist.name}</span>
              </label>
            ))}
          </div>
          <button
            className="inspection-journey__button"
            disabled={
              busy || !selectedTemplateId || selectedChecklists.length === 0
            }
            onClick={() =>
              run(
                () => saveCycleEngagementChecklists(linkId, selectedChecklists),
                "Checklist selection saved."
              )
            }
          >
            Save Checklists
          </button>
        </section>
        <section className="inspection-journey__panel">
          <header>
            <div>
              <h2>3. User Assignments</h2>
              <p>
                The primary reviewer is assigned directly to each selected
                checklist.
              </p>
            </div>
          </header>
          <select
            className="form-select"
            value={reviewerId}
            disabled={busy}
            onChange={(event) => setReviewerId(event.target.value)}
          >
            <option value="">Select primary reviewer</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.fullName}
              </option>
            ))}
          </select>
          <button
            className="inspection-journey__button"
            disabled={busy || !reviewerId || !link.checklists?.length}
            onClick={saveTeam}
          >
            Save Assignments
          </button>
        </section>
        <section className="inspection-journey__panel">
          <header>
            <div>
              <h2>4. Create Inspection Review</h2>
              <p>
                The review due date will be copied from the current cycle end
                date. Later cycle changes will not alter it.
              </p>
            </div>
          </header>
          <button
            className="inspection-journey__button is-primary"
            disabled={
              busy ||
              !link.reviewTemplateId ||
              !link.checklists?.length ||
              !link.assignments?.length
            }
            onClick={createReview}
          >
            {busy ? "Working…" : "Create Review"}
          </button>
        </section>
      </div>
    </section>
  );
}
